import { Routes, Route, Navigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { useTelegram, useStore } from '@/core/providers';
import { authService } from '@/core/api';
import Layout from '@/components/Layout';
import FeedPage from '@/features/feed/FeedPage';
import FeedDetailPage from '@/features/feed/FeedDetailPage';
import SearchPage from '@/features/search/SearchPage';
import CreateTaskPage from '@/features/tracking/CreateTaskPage';
import MyTasksPage from '@/features/tracking/MyTasksPage';
import TaskDetailPage from '@/features/tracking/TaskDetailPage';
import SettingsPage from '@/features/settings/SettingsPage';
import LoginPage from '@/features/auth/LoginPage';
import OnboardingPage from '@/features/onboarding/OnboardingPage';

function App() {
  const telegram = useTelegram();
  const { user, setUser, isAuthenticated } = useStore();
  const [authState, setAuthState] = useState<'loading' | 'authenticated' | 'unauthenticated'>('loading');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let mounted = true;

    const initAuth = async () => {
      console.log('[Auth] Starting initialization...');
      console.log('[Auth] Telegram ready:', telegram.isReady);
      console.log('[Auth] Telegram initData:', telegram.initData ? 'present' : 'absent');
      console.log('[Auth] Telegram user:', telegram.user);

      // Wait for Telegram SDK to be ready
      await new Promise(resolve => setTimeout(resolve, 200));

      if (!mounted) return;

      try {
        // Check if already authenticated from store
        if (user) {
          console.log('[Auth] User already in store:', user.email);
          setAuthState('authenticated');
          return;
        }

        // Check for existing token
        const token = localStorage.getItem('access_token');
        if (token) {
          console.log('[Auth] Found existing token, validating...');
          try {
            const authUser = await authService.getMe();
            if (!mounted) return;
            console.log('[Auth] Token validated successfully:', authUser.email);
            setUser(authUser);
            setAuthState('authenticated');
            return;
          } catch (error) {
            console.error('[Auth] Token validation failed:', error);
            localStorage.removeItem('access_token');
            localStorage.removeItem('refresh_token');
            // Continue to try Telegram auth or show login
          }
        }

        // Try Telegram authentication
        if (telegram.isReady && telegram.initData && telegram.user) {
          console.log('[Auth] Attempting Telegram authentication...');
          console.log('[Auth] InitData length:', telegram.initData.length);
          console.log('[Auth] User ID:', telegram.user.id);

          try {
            const response = await authService.telegramAuth(telegram.initData);
            if (!mounted) return;

            console.log('[Auth] Telegram auth successful');
            localStorage.setItem('access_token', response.access_token);
            localStorage.setItem('refresh_token', response.refresh_token);
            setUser(response.user);
            setAuthState('authenticated');
            return;
          } catch (error: any) {
            console.error('[Auth] Telegram auth failed:', error);
            console.error('[Auth] Error response:', error.response?.data);
            console.error('[Auth] Error status:', error.response?.status);
            setError(`Telegram auth failed: ${error.response?.data?.error || error.message}`);
            // Fall through to unauthenticated state
          }
        } else {
          console.log('[Auth] No Telegram environment detected');
          console.log('[Auth] - isReady:', telegram.isReady);
          console.log('[Auth] - initData:', telegram.initData);
          console.log('[Auth] - user:', telegram.user);
        }

        // No auth available
        console.log('[Auth] No authentication available, showing login');
        setAuthState('unauthenticated');

      } catch (error) {
        console.error('[Auth] Unexpected error during initialization:', error);
        setError('Authentication initialization failed');
        setAuthState('unauthenticated');
      }
    };

    initAuth();

    return () => {
      mounted = false;
    };
  }, []); // Run only once on mount

  // Loading state
  if (authState === 'loading') {
    return (
      <div className="h-screen flex items-center justify-center bg-telegram-bg">
        <div className="text-center">
          <div className="animate-spin w-12 h-12 border-3 border-telegram-button border-t-transparent rounded-full mx-auto mb-4" />
          <p className="text-telegram-hint text-sm">Initializing...</p>
          {error && (
            <p className="text-red-500 text-xs mt-2 max-w-md px-4">{error}</p>
          )}
        </div>
      </div>
    );
  }

  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/onboarding" element={<OnboardingPage />} />
      <Route element={<Layout />}>
        <Route path="/" element={<Navigate to="/feed" replace />} />
        <Route path="/feed" element={<FeedPage />} />
        <Route path="/feed/:id" element={<FeedDetailPage />} />
        <Route path="/search" element={<SearchPage />} />
        <Route
          path="/create"
          element={
            isAuthenticated ? <CreateTaskPage /> : <Navigate to="/login" replace />
          }
        />
        <Route
          path="/tasks"
          element={
            isAuthenticated ? <MyTasksPage /> : <Navigate to="/login" replace />
          }
        />
        <Route
          path="/tasks/:taskId"
          element={
            isAuthenticated ? <TaskDetailPage /> : <Navigate to="/login" replace />
          }
        />
        <Route
          path="/bookmarks"
          element={<Navigate to="/feed" replace />}
        />
        <Route path="/settings" element={<SettingsPage />} />
      </Route>
    </Routes>
  );
}

export default App;
